import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { pdfStyles } from '../../..';
import { InvoiceInterface } from '../../../../interfaces/invoice.interface';
import { getDateOfDoc } from '../../../../services/utils/pdf';

interface Props {
  doc: InvoiceInterface;
}

export const PdfDocTitle = ({ doc }: Props) => {
  const { t } = useTranslation();

  const getDate = useCallback(() => {
    return getDateOfDoc(doc?.documentDate, doc?.sentToAadeAt);
  }, [doc?.documentDate, doc?.sentToAadeAt]);

  const getDeliveryNoteMark = useCallback(() => {
    if (doc?.isDeliveryNote) return ` - ${t('DOC_TYPES.DELIVERY_NOTE_SHORT')}`;
  }, [doc?.isDeliveryNote, t]);

  const getTransitLabel = useCallback(() => {
    const labels = [];
    if (doc?.nonObligatedRecipient) labels.push(t('FORMS.NON_OBLIGATED_RECIPIENT'));
    if (doc?.withoutDigitalTransportTracking) labels.push(t('FORMS.WITHOUT_DIGITAL_TRANSPORT_TRACKING'));

    const labelToShow = labels.join(' - ');
    if (labelToShow) {
      return <Text style={[pdfStyles.fontNormal7, { paddingLeft: 2 }]}>{`(${labelToShow})`}</Text>;
    }
  }, [doc?.nonObligatedRecipient, doc?.withoutDigitalTransportTracking, t]);

  return (
    <View style={styles.row}>
      <View style={styles.column}>
        <Text style={pdfStyles.smallTitle}>{t('PDF.DOC_TYPE')}</Text>
        <View style={pdfStyles.flexStartCenterRow}>
          <Text style={[pdfStyles.body1, { paddingRight: 1 }]}>
            {t(`DOC_TYPES.${doc?.documentType?.name}`)}
            {getDeliveryNoteMark()}
          </Text>
          {getTransitLabel()}
        </View>
      </View>

      <View style={[styles.columnCenter]}>
        <Text style={pdfStyles.smallTitle}>
          {t('PDF.SERIES')} - {t('PDF.SERIAL_NUMBER')}
        </Text>
        <View style={styles.flexCenterSpaceRow}>
          <Text style={pdfStyles.body1}>{doc?.seriesName || '0'}</Text>
          <Text>-</Text>
          <Text style={pdfStyles.body1}>{doc?.serialNumber || 'empty'}</Text>
        </View>
      </View>

      <View style={styles.columnEnd}>
        <Text style={pdfStyles.smallTitle}>{t('PDF.DATE')}</Text>
        <Text style={pdfStyles.body1}>{getDate()}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  column: {
    flexDirection: 'column',
    width: '33%',
    alignItems: 'flex-start',
  },
  columnCenter: {
    flexDirection: 'column',
    width: '33%',
    alignItems: 'center',
  },
  columnEnd: {
    flexDirection: 'column',
    width: '33%',
    alignItems: 'flex-end',
  },
  flexCenterSpaceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontFamily: 'Manrope',
    fontWeight: 'bold',
  },
  regularTitle: {
    fontSize: 10,
  },
  smallTitle: {
    fontSize: 8,
    fontWeight: 'bold',
  },
});
